import React, { useEffect, useRef, useState } from "react";
import { Edit, Plus, Save, Trash2, X, Repeat1 } from "lucide-react";
import type { DataTypeItem, Attribute } from "@/shared/partnerschema";
import { useGetDataTypes, useGetAttributes } from "@/hooks/useLocalStorage";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpressionRow {
    id: number;
    startGroup: string;
    operator: string;
    expressionType: "value" | "operand";
    expressionValue?: string;
    dataType?: string;
    attribute?: string;
    endGroup: string;
    saved?: boolean;
    addRootMode?: boolean;
    addRootValue?: string;
    addRootValueDT?: string;
    addRootValueAttr?: string;
    addRootType?: "value" | "operand";
}

interface ExpressionBuilderModalProps {
    open: boolean;
    onClose: () => void;
    initialExpression?: string;
    initialExpressionJson?: any[] | null;
    onSave: (data: {
        finalExpression: string;
        expressionJson: {
            StartGroup: string;
            EndGroup: string;
            Operator: string;
            Value: string;
            DataType: string;
            Attribute: string;
            RootValue: string;
            RootDataType: string;
            RootAttribute: string;
        }[];
    }) => void;
}

const BLANK_ROW = (): ExpressionRow => ({
    id: Date.now(),
    startGroup: "",
    operator: "",
    expressionType: "value",
    expressionValue: "",
    dataType: "",
    attribute: "",
    endGroup: "",
    saved: false,
    addRootValue: "",
    addRootValueDT: "",
    addRootValueAttr: "",
});

// ─── Component ────────────────────────────────────────────────────────────────

export const ExpressionBuilderModal: React.FC<ExpressionBuilderModalProps> = ({
    open,
    onClose,
    initialExpressionJson = null,
    onSave,
}) => {
    const [rows, setRows] = useState<ExpressionRow[]>([BLANK_ROW()]);
    const [attrCode, setAttrCode] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const hasInitialized = useRef(false);

    // ── Data ──────────────────────────────────────────────────────────────────
    const { data: dtData } = useGetDataTypes();
    const dataType: DataTypeItem[] = dtData?.items ?? [];

    const { data: attrData } = useGetAttributes(attrCode);
    const attribute: Attribute[] = attrData?.items ?? [];

    // ── Init from JSON ────────────────────────────────────────────────────────

    const parseInitialData = (jsonData: any[]): ExpressionRow[] =>
        jsonData.map((item, index) => ({
            id: index,
            startGroup: item.StartGroup || "",
            operator: item.Operator || "",
            expressionType: item.Value ? "value" : "operand",
            expressionValue: item.Value || "",
            dataType: item.DataType || "",
            attribute: item.Attribute || "",
            endGroup: item.EndGroup || "",
            saved: true,
            addRootMode: !!(item.RootValue || item.RootDataType),
            addRootValue: item.RootValue || "",
            addRootValueDT: item.RootDataType || "",
            addRootValueAttr: item.RootAttribute || "",
            addRootType: item.RootValue ? "value" : item.RootDataType ? "operand" : "value",
        }));

    useEffect(() => {
        if (!open) { hasInitialized.current = false; return; }
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        if (initialExpressionJson && initialExpressionJson.length > 0) {
            setRows(parseInitialData(initialExpressionJson));
        } else {
            setRows([BLANK_ROW()]);
        }
    }, [open, initialExpressionJson]);

    // ── Row helpers ───────────────────────────────────────────────────────────

    const handleRowChange = (id: number, key: keyof ExpressionRow, value: any) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                if (key === "addRootMode") {
                    return { ...r, [key]: value, addRootValue: "", addRootValueDT: "", addRootValueAttr: "", saved: false };
                }
                return { ...r, [key]: value?.Name ?? value?.name ?? value, saved: false };
            })
        );
        if (key === "dataType" || key === "addRootValueDT") {
            const selected = dataType.find(d => d.Name === (value?.Name ?? value));
            setAttrCode(String(selected?.ID ?? ""));
        }
    };

    const addRow = () => setRows((prev) => [...prev, BLANK_ROW()]);
    const deleteRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id));
    const saveRow = (id: number) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, saved: true } : r));
    const editRow = (id: number) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, saved: false } : r));

    const toggleExpressionType = (id: number, isRoot = false) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                if (isRoot) {
                    return { ...r, addRootType: r.addRootType === "value" ? "operand" : "value", addRootValue: "", addRootValueDT: "", addRootValueAttr: "" };
                }
                return { ...r, expressionType: r.expressionType === "value" ? "operand" : "value", expressionValue: "", dataType: "", attribute: "" };
            })
        );
    };

    // ── Build / validate ──────────────────────────────────────────────────────

    const buildFinalExpression = () =>
        rows.map((r) => {
            const expr = r.expressionType === "value"
                ? r.expressionValue
                : `${r.dataType || ""}.${r.attribute || ""}`;
            const rootExpr = r.addRootMode && (
                r.addRootType === "value" ? r.addRootValue : `${r.addRootValueDT || ""}.${r.addRootValueAttr || ""}`
            );
            return `${r.startGroup || ""} ${r.operator || ""} ${expr || ""} ${r.endGroup || ""} ${rootExpr ? `r${rootExpr}` : ""}`;
        }).join(" ");

    const validateExpression = (expr: string): string | null => {
        const stack: string[] = [];
        for (const ch of expr) {
            if (ch === "(") stack.push(ch);
            else if (ch === ")") { if (!stack.length) return "Unbalanced parentheses"; stack.pop(); }
        }
        if (stack.length) return "Unbalanced parentheses";
        if (/[\+\-\*\/%]{2,}/.test(expr)) return "Invalid consecutive operators";
        if (/^[\+\*\/%]/.test(expr.trim())) return "Expression cannot start with an operator";
        if (/[\+\-\*\/%]\s*$/.test(expr.trim())) return "Expression cannot end with an operator";
        return null;
    };

    const handleSaveAll = () => {
        const finalExpression = buildFinalExpression().replace(/\s+/g, " ").trim();
        const error = validateExpression(finalExpression);
        if (error) { setErrorMsg(error); return; }
        setErrorMsg("");

        const expressionJson = rows.map((r) => ({
            StartGroup: r.startGroup || "",
            EndGroup: r.endGroup || "",
            Operator: r.operator || "",
            Value: r.expressionType === "value" ? r.expressionValue || "" : "",
            DataType: r.expressionType === "operand" ? r.dataType || "" : "",
            Attribute: r.expressionType === "operand" ? r.attribute || "" : "",
            RootValue: r.addRootType === "value" ? r.addRootValue || "" : "",
            RootDataType: r.addRootType === "operand" ? r.addRootValueDT || "" : "",
            RootAttribute: r.addRootType === "operand" ? r.addRootValueAttr || "" : "",
        }));

        onSave({ finalExpression: finalExpression.trim(), expressionJson });
        setRows([BLANK_ROW()]);
        onClose();
    };

    const handleClose = () => { setErrorMsg(""); setAttrCode(""); onClose(); };

    // ── Shared select classname ───────────────────────────────────────────────

    const DtSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="w-full">
                <SelectValue placeholder="Select DataType" />
            </SelectTrigger>
            <SelectContent>
                {dataType.map((opt) => (
                    <SelectItem key={opt.Name} value={opt.Name}>{opt.Name}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );

    const AttrSelect = ({ value, disabled, onChange }: { value: string; disabled?: boolean; onChange: (v: string) => void }) => (
        <Select value={value} onValueChange={onChange} disabled={disabled || attribute.length === 0}>
            <SelectTrigger className="w-full">
                <SelectValue placeholder={attribute.length === 0 ? "No Attributes" : "Select Attribute"} />
            </SelectTrigger>
            <SelectContent>
                {attribute.map((opt) => (
                    <SelectItem key={opt.name} value={opt.name}>{opt.name}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-[95vw] w-auto min-w-[800px] max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Expression Builder</DialogTitle>
                </DialogHeader>

                <div className="border rounded-md overflow-auto flex-1">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="bg-gray-100 text-gray-700">
                                <th className="px-3 py-2 text-left font-medium border-b whitespace-nowrap">Start Group</th>
                                <th className="px-3 py-2 text-left font-medium border-b">Operator</th>
                                <th className="px-3 py-2 text-left font-medium border-b">Expression</th>
                                <th className="px-3 py-2 text-left font-medium border-b">End Group</th>
                                <th className="px-3 py-2 text-left font-medium border-b">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr key={row.id} className="border-b last:border-0 hover:bg-gray-50">
                                    {!row.saved ? (
                                        <>
                                            {/* Start Group */}
                                            <td className="px-3 py-2">
                                                <Select value={row.startGroup} onValueChange={(v) => handleRowChange(row.id, "startGroup", v)}>
                                                    <SelectTrigger className="w-20"><SelectValue placeholder="None" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value=" ">None</SelectItem>
                                                        <SelectItem value="(">(</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </td>

                                            {/* Operator */}
                                            <td className="px-3 py-2">
                                                <Select value={row.operator} onValueChange={(v) => handleRowChange(row.id, "operator", v)}>
                                                    <SelectTrigger className="w-24"><SelectValue placeholder="Select" /></SelectTrigger>
                                                    <SelectContent>
                                                        {["+", "-", "*", "/", "%"].map(op => (
                                                            <SelectItem key={op} value={op}>{op}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </td>

                                            {/* Expression */}
                                            <td className="px-3 py-2">
                                                {row.expressionType === "value" ? (
                                                    <Input
                                                        value={row.expressionValue}
                                                        onChange={(e) => handleRowChange(row.id, "expressionValue", e.target.value)}
                                                        placeholder="Enter value"
                                                        className="w-full"
                                                    />
                                                ) : (
                                                    <div className="flex gap-2">
                                                        <DtSelect
                                                            value={row.dataType || ""}
                                                            onChange={(v) => {
                                                                const selected = dataType.find(d => d.Name === v);
                                                                handleRowChange(row.id, "dataType", selected || v);
                                                            }}
                                                        />
                                                        <AttrSelect
                                                            value={row.attribute || ""}
                                                            disabled={!row.dataType}
                                                            onChange={(v) => handleRowChange(row.id, "attribute", v)}
                                                        />
                                                    </div>
                                                )}
                                            </td>

                                            {/* End Group */}
                                            <td className="px-3 py-2">
                                                <div className="flex flex-col gap-1">
                                                    <Select
                                                        value={row.endGroup || ""}
                                                        onValueChange={(v) => {
                                                            handleRowChange(row.id, "endGroup", v);
                                                            handleRowChange(row.id, "addRootMode", v === ")");
                                                        }}
                                                    >
                                                        <SelectTrigger className="w-20"><SelectValue placeholder="None" /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value=" ">None</SelectItem>
                                                            <SelectItem value=")">)</SelectItem>
                                                        </SelectContent>
                                                    </Select>

                                                    {row.addRootMode && (
                                                        <div className="flex flex-col gap-1">
                                                            {row.addRootType === "value" ? (
                                                                <Input
                                                                    value={row.addRootValue || ""}
                                                                    onChange={(e) => handleRowChange(row.id, "addRootValue", e.target.value)}
                                                                    placeholder="Root value"
                                                                />
                                                            ) : (
                                                                <div className="flex gap-2">
                                                                    <DtSelect
                                                                        value={row.addRootValueDT || ""}
                                                                        onChange={(v) => {
                                                                            const selected = dataType.find(d => d.Name === v);
                                                                            handleRowChange(row.id, "addRootValueDT", selected || v);
                                                                        }}
                                                                    />
                                                                    <AttrSelect
                                                                        value={row.addRootValueAttr || ""}
                                                                        disabled={!row.addRootValueDT}
                                                                        onChange={(v) => handleRowChange(row.id, "addRootValueAttr", v)}
                                                                    />
                                                                </div>
                                                            )}
                                                            <div className="flex gap-1">
                                                                <button type="button" title="Toggle type" onClick={() => toggleExpressionType(row.id, true)} className="p-1 rounded hover:bg-gray-100">
                                                                    <Repeat1 className="h-4 w-4 text-gray-700" />
                                                                </button>
                                                                <button type="button" title="Cancel" onClick={() => handleRowChange(row.id, "addRootMode", false)} className="p-1 rounded hover:bg-gray-100">
                                                                    <X className="h-4 w-4 text-red-500" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Actions */}
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1">
                                                    <button type="button" title="Toggle expression type" onClick={() => toggleExpressionType(row.id)} className="p-1 rounded hover:bg-gray-100">
                                                        <Repeat1 className="h-4 w-4 text-gray-700" />
                                                    </button>
                                                    <button type="button" title="Save" onClick={() => saveRow(row.id)} className="p-1 rounded hover:bg-gray-100">
                                                        <Save className="h-4 w-4 text-gray-700" />
                                                    </button>
                                                    {idx > 0 && (
                                                        <button type="button" title="Remove row" onClick={() => deleteRow(row.id)} className="p-1 rounded hover:bg-gray-100">
                                                            <X className="h-4 w-4 text-gray-700" />
                                                        </button>
                                                    )}
                                                    <button type="button" title="Add Row" onClick={addRow} className="p-1 rounded hover:bg-gray-100">
                                                        <Plus className="h-4 w-4 text-gray-700" />
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        /* Saved / read-only row */
                                        <>
                                            <td className="px-3 py-2 text-sm">{row.startGroup}</td>
                                            <td className="px-3 py-2 text-sm">{row.operator}</td>
                                            <td className="px-3 py-2 text-sm">
                                                {row.expressionType === "value"
                                                    ? row.expressionValue
                                                    : row.dataType && `${row.dataType}.${row.attribute}`}
                                            </td>
                                            <td className="px-3 py-2 text-sm">
                                                {row.endGroup}
                                                {row.addRootType === "value"
                                                    ? row.addRootValue && `r${row.addRootValue}`
                                                    : row.addRootValueDT && `r${row.addRootValueDT}${row.addRootValueAttr ? "." + row.addRootValueAttr : ""}`}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1">
                                                    <button type="button" title="Edit" onClick={() => editRow(row.id)} className="p-1 rounded hover:bg-gray-100">
                                                        <Edit className="h-4 w-4 text-gray-700" />
                                                    </button>
                                                    {idx > 0 && (
                                                        <button type="button" title="Delete Row" onClick={() => deleteRow(row.id)} className="p-1 rounded hover:bg-gray-100">
                                                            <Trash2 className="h-4 w-4 text-red-500" />
                                                        </button>
                                                    )}
                                                    <button type="button" title="Add Row" onClick={addRow} className="p-1 rounded hover:bg-gray-100">
                                                        <Plus className="h-4 w-4 text-gray-700" />
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {errorMsg && (
                    <p className="text-red-500 text-sm mt-2 text-center">{errorMsg}</p>
                )}

                <DialogFooter>
                    <Button onClick={handleSaveAll}>Save Expression</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};